import Phaser from 'phaser';
import { ITEM_DEFINITIONS, LOOT_TABLES, SLOT_LABELS, CONSUMABLE_DEFINITIONS, CONSUMABLE_LOOT, BOSS_SIGNATURE_ITEMS } from '../data/items';
import { ItemDefinition, PERSONAL_SLOTS } from '../types/Item.types';
import { InventoryManager } from '../systems/InventoryManager';
import { RunManager } from '../systems/RunManager';
import { CodexManager } from '../systems/CodexManager';
import { rollLootPool, maxPicksForContext, CombatLootContext, LootRarity } from '../systems/LootRoller';
import { RARITY_COLORS } from '../ui/InventoryUI';
import { DiscardItemModal } from '../ui/DiscardItemModal';
import { ensureTouchTarget } from '../ui/MobileTouchUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE DE RÉCOMPENSE ===
// Après victoire : choisir UN item OU se soigner. Un seul choix possible.
// Utilise InventoryManager comme source de vérité unique.

export class LootScene extends Phaser.Scene {
  private run!: RunManager;
  private inv!: InventoryManager;
  private lootItems: ItemDefinition[] = [];
  private chosenCount = 0;
  private maxPicks = 1;
  private context: CombatLootContext = 'normal';
  // Sprint 76 fix : tracker les choix faits pour empêcher les re-picks après redessin (boss = 2 picks)
  private pickedHeal = false;
  private pickedConsumable = false;
  private pickedItemIndices: Set<number> = new Set();
  // Bug fix v0.36.8 : permettre de rediriger après pick (boss final → RunVictoryScene)
  private nextSceneAfter = 'EquipScene';

  constructor() {
    super({ key: 'LootScene' });
  }

  /** Helper rétro-compat : true si le joueur a déjà atteint son quota de picks. */
  private get hasChosen(): boolean {
    return this.chosenCount >= this.maxPicks;
  }

  init(data: { run?: RunManager; nextSceneAfter?: string }): void {
    this.run = data?.run ?? new RunManager();
    this.inv = this.run.getInventory();
    this.chosenCount = 0;
    this.pickedHeal = false;
    this.pickedConsumable = false;
    this.pickedItemIndices.clear();
    this.nextSceneAfter = data?.nextSceneAfter ?? 'EquipScene';

    // Détermine le contexte de loot selon le type de combat qu'on vient de gagner
    const nodeType = this.run.getCurrentNodeType();
    if (nodeType === 'boss') this.context = 'boss';
    else if (nodeType === 'elite') this.context = 'elite';
    else this.context = 'normal';
    this.maxPicks = maxPicksForContext(this.context);
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.lootItems = this.generateLoot();
    this.drawUI();
  }

  private generateLoot(): ItemDefinition[] {
    const meta = this.run.getMeta();
    const count = 3;
    const items: ItemDefinition[] = [];

    // Filtrer les items verrouillés
    const filterUnlocked = (ids: readonly string[]) =>
      ids.filter(id => meta.isItemUnlocked(id));

    const pools: Record<LootRarity, string[]> = {
      common: filterUnlocked(LOOT_TABLES.common),
      uncommon: filterUnlocked(LOOT_TABLES.uncommon),
      rare: filterUnlocked(LOOT_TABLES.rare),
    };

    // Pondération par contexte de combat (Sprint 76) : normal=70/25/5, elite=35/45/20, boss=15/50/35
    const rarities = rollLootPool(this.context, count);

    for (const rarity of rarities) {
      let pool = pools[rarity];
      // Fallback si rareté vide : retomber sur commun puis sur le commun par défaut
      if (pool.length === 0) pool = pools.common.length > 0 ? pools.common : [...LOOT_TABLES.common];
      const id = pool[Phaser.Math.Between(0, pool.length - 1)];
      items.push(ITEM_DEFINITIONS[id]);
    }

    // Sprint 77 — Items signatures boss-only : 50% chance que l'item du boss tué
    // remplace le 1er item du pool (le plus visible). Garantit que le joueur peut
    // farmer le boss pour son trophée (sans que ce soit anecdotique ni systématique).
    if (this.context === 'boss' && Math.random() < 0.5) {
      const bossSignature = this.findBossSignatureItem();
      if (bossSignature) items[0] = bossSignature;
    }

    return items;
  }

  /** Cherche l'item signature du boss vaincu dans la config de combat. */
  private findBossSignatureItem(): ItemDefinition | null {
    const config = this.run.getCurrentCombatConfig();
    if (!config?.isBoss) return null;
    for (const enemy of config.enemies) {
      const sigId = BOSS_SIGNATURE_ITEMS[enemy.type];
      if (sigId && ITEM_DEFINITIONS[sigId]) return ITEM_DEFINITIONS[sigId];
    }
    return null;
  }

  private drawUI(): void {
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    const titleEmoji = this.context === 'boss' ? '🏆 BUTIN BOSS' : this.context === 'elite' ? '💀 BUTIN ÉLITE' : '🏆 RÉCOMPENSE';
    this.add.text(GAME_CX, 40, titleEmoji, {
      fontSize: '22px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    const subtitle = this.maxPicks > 1
      ? `Choisis jusqu'à ${this.maxPicks} récompenses (${this.maxPicks - this.chosenCount} restantes)`
      : 'Choisis UNE récompense (un seul choix !)';
    this.add.text(GAME_CX, 70, subtitle, {
      fontSize: '13px', color: '#e74c3c', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    // Légende des raretés
    this.add.text(GAME_CX - 150, 92, '⬜ Commun', {
      fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
    }).setOrigin(0.5);
    this.add.text(GAME_CX, 92, '🟢 Peu commun', {
      fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
    }).setOrigin(0.5);
    this.add.text(GAME_CX + 140, 92, '🟡 Rare', {
      fontSize: '10px', color: '#f1c40f', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // HP actuel du premier joueur vivant
    const team = this.run.getAliveTeam();
    const firstMember = team.length > 0 ? team[0] : null;
    const maxHp = firstMember ? firstMember.maxHp : 20;
    const currentHp = this.run.getPlayerHp() > 0 ? this.run.getPlayerHp() : maxHp;
    const missingHp = maxHp - currentHp;
    const healAmount = Math.min(missingHp, Math.ceil(maxHp * 0.4));

    // --- Option Soin ---
    const healY = 108;
    this.drawHealOption(healY, currentHp, maxHp, healAmount);

    // --- Séparateur ---
    this.add.text(GAME_CX, healY + 70, '── ou ──', {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // --- Items de loot ---
    const startY = healY + 100;
    const itemHeight = 80;

    for (let i = 0; i < this.lootItems.length; i++) {
      const item = this.lootItems[i];
      const y = startY + i * itemHeight;
      this.drawLootItem(item, y, i);
    }

    // --- Consommable aléatoire ---
    const consY = startY + this.lootItems.length * itemHeight;
    this.drawConsumableOption(consY);

    // --- Bouton passer / terminer (selon que des picks ont déjà été pris) ---
    const skipY = consY + itemHeight + 20;
    const skipLabel = this.chosenCount > 0
      ? `[ Terminer (${this.chosenCount}/${this.maxPicks} pris) → ]`
      : '[ Passer sans rien prendre → ]';
    const skipColor = this.chosenCount > 0 ? '#2ecc71' : '#7f8c8d';
    const skipBtn = this.add.text(GAME_CX, skipY, skipLabel, {
      fontSize: '12px', color: skipColor, fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 10, y: 5 },
    }).setOrigin(0.5);
    skipBtn.setInteractive({ useHandCursor: true });
    skipBtn.on('pointerdown', () => this.confirmChoice());
    skipBtn.on('pointerover', () => skipBtn.setColor('#7f8c8d'));
    skipBtn.on('pointerout', () => skipBtn.setColor('#7f8c8d'));

    // --- Inventaire actuel ---
    this.drawInventorySummary(skipY + 40);
  }

  private drawHealOption(y: number, currentHp: number, maxHp: number, healAmount: number): void {
    const cardW = Math.min(520, GAME_W - 20);
    const lx = Math.round(GAME_CX - cardW / 2 + 10);
    const rx = Math.round(GAME_CX + cardW / 2 - 10);
    const bg = this.add.rectangle(GAME_CX, y + 22, cardW, 55, 0x1a2a1a);
    bg.setStrokeStyle(2, 0x2ecc71);

    this.add.text(lx, y + 5, `🩹 Repos — Soigner ${healAmount} HP`, {
      fontSize: '14px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
    });

    this.add.text(lx, y + 27, `HP actuel: ${currentHp}/${maxHp}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    });

    if (currentHp >= maxHp) {
      this.add.text(rx, y + 15, 'HP max !', {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(1, 0.5);
    } else if (this.pickedHeal) {
      // Déjà pris ce pick (boss multi-pick) → bouton désactivé visuellement
      this.add.text(rx, y + 15, '✓ Pris !', {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(1, 0.5);
      bg.setFillStyle(0x0a2a0a);
    } else {
      const healBtn = this.add.text(rx, y + 10, '[ Se soigner ]', {
        fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#0a1a0a', padding: { x: 6, y: 3 },
      }).setOrigin(1, 0);
      healBtn.setInteractive({ useHandCursor: true });
      healBtn.on('pointerdown', () => {
        if (this.hasChosen || this.pickedHeal) return;
        this.pickedHeal = true;

        const newHp = Math.min(maxHp, currentHp + healAmount);
        this.run.setPlayerHp(newHp);

        healBtn.setText(`✓ +${healAmount} HP`);
        healBtn.setColor('#7f8c8d');
        healBtn.removeInteractive();
        bg.setFillStyle(0x0a2a0a);

        this.recordPick();
      });
      healBtn.on('pointerover', () => { if (!this.hasChosen) healBtn.setColor('#27ae60'); });
      healBtn.on('pointerout', () => { if (!this.hasChosen) healBtn.setColor('#2ecc71'); });
      ensureTouchTarget(healBtn);
    }
  }

  private drawLootItem(item: ItemDefinition, y: number, index: number): void {
    const cardW = Math.min(520, GAME_W - 20);
    const lx = Math.round(GAME_CX - cardW / 2 + 10);
    const rx = Math.round(GAME_CX + cardW / 2 - 10);
    const rarityColor = RARITY_COLORS[item.rarity];

    const bg = this.add.rectangle(GAME_CX, y + 22, cardW, 60, 0x1e2d4a);
    bg.setStrokeStyle(2, parseInt(rarityColor.replace('#', ''), 16));

    this.add.text(lx, y + 5, `${item.emoji} ${item.name}`, {
      fontSize: '14px', color: rarityColor, fontFamily: FONT_BODY, fontStyle: 'bold',
    });

    this.add.text(lx, y + 25, item.description, {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
      wordWrap: { width: cardW - 40 },
    });

    this.add.text(rx, y + 5, SLOT_LABELS[item.slot] ?? item.slot, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(1, 0);

    // Sprint 76 fix : si déjà pris (boss multi-pick), bouton désactivé
    if (this.pickedItemIndices.has(index)) {
      this.add.text(rx, y + 23, '✓ Pris !', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
        backgroundColor: '#1a1a2e', padding: { x: 6, y: 3 },
      }).setOrigin(1, 0);
      bg.setFillStyle(0x1a3a2a);
      return;
    }

    // Vérifier si le sac est plein
    const isFull = !this.inv.hasTransportSpace();

    const pickBtn = this.add.text(rx, y + 23, isFull ? '🎒 Faire de la place' : '[ Prendre ]', {
      fontSize: '12px', color: isFull ? '#e67e22' : '#f1c40f', fontFamily: FONT_BODY,
      backgroundColor: '#2a2a0a', padding: { x: 6, y: 3 },
    }).setOrigin(1, 0);
    pickBtn.setInteractive({ useHandCursor: true });

    /** Effectue la prise effective de l'item — appelé direct ou après libération de place. */
    const doPickup = () => {
      if (this.hasChosen || this.pickedItemIndices.has(index)) return;
      this.pickedItemIndices.add(index);
      this.inv.pickLootItem(item.id);
      this.run.getMeta().addStat('itemsLooted');
      new CodexManager().discoverItem(item.id);
      pickBtn.setText('✓ Pris !');
      pickBtn.setColor('#7f8c8d');
      pickBtn.removeInteractive();
      bg.setFillStyle(0x1a3a2a);
      this.recordPick();
    };

    pickBtn.on('pointerdown', () => {
      if (this.hasChosen || this.pickedItemIndices.has(index)) return;
      if (!this.inv.hasTransportSpace()) {
        // Sac plein → ouvrir la modale de destruction, puis re-tenter le pickup
        const modal = new DiscardItemModal(this, this.inv, {
          category: 'transport',
          onAfterDiscard: () => {
            if (this.inv.hasTransportSpace()) doPickup();
          },
        });
        modal.show();
        return;
      }
      doPickup();
    });
    pickBtn.on('pointerover', () => { if (!this.hasChosen) pickBtn.setColor('#ffffff'); });
    pickBtn.on('pointerout', () => { if (!this.hasChosen) pickBtn.setColor(isFull ? '#e67e22' : '#f1c40f'); });
    ensureTouchTarget(pickBtn);
  }

  private drawConsumableOption(y: number): void {
    // Tirer un consommable aléatoire
    const roll = Math.random();
    const pool = roll < 0.6 ? CONSUMABLE_LOOT.common : CONSUMABLE_LOOT.uncommon;
    const consId = pool[Phaser.Math.Between(0, pool.length - 1)];
    const def = CONSUMABLE_DEFINITIONS[consId];
    if (!def) return;

    const cardW = Math.min(520, GAME_W - 20);
    const lx = Math.round(GAME_CX - cardW / 2 + 10);
    const rx = Math.round(GAME_CX + cardW / 2 - 10);
    const rarityColor = RARITY_COLORS[def.rarity];
    const bg = this.add.rectangle(GAME_CX, y + 22, cardW, 60, 0x2d1e4a);
    bg.setStrokeStyle(2, parseInt(rarityColor.replace('#', ''), 16));

    this.add.text(lx, y + 5, `${def.emoji} ${def.name}`, {
      fontSize: '14px', color: rarityColor, fontFamily: FONT_BODY, fontStyle: 'bold',
    });
    this.add.text(lx, y + 25, def.description, {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
      wordWrap: { width: cardW - 40 },
    });
    this.add.text(rx, y + 5, 'Consommable', {
      fontSize: '10px', color: '#9b59b6', fontFamily: FONT_BODY,
    }).setOrigin(1, 0);

    // Sprint 76 fix : si déjà pris (boss multi-pick), bouton désactivé
    if (this.pickedConsumable) {
      this.add.text(rx, y + 23, '✓ Pris !', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
        backgroundColor: '#1a1a2e', padding: { x: 6, y: 3 },
      }).setOrigin(1, 0);
      bg.setFillStyle(0x1a2a3a);
      return;
    }

    // Vérifier si le pool de consommables est plein
    const consFull = this.inv.getConsumables().length >= this.inv.getMaxConsumables();

    const pickBtn = this.add.text(rx, y + 23, consFull ? '🧪 Faire de la place' : '[ Prendre ]', {
      fontSize: '12px', color: consFull ? '#e67e22' : '#f1c40f', fontFamily: FONT_BODY,
      backgroundColor: '#2a2a0a', padding: { x: 6, y: 3 },
    }).setOrigin(1, 0);
    pickBtn.setInteractive({ useHandCursor: true });

    /** Effectue la prise effective du consommable — appelé direct ou après libération. */
    const doPickup = () => {
      if (this.hasChosen || this.pickedConsumable) return;
      this.pickedConsumable = true;
      this.inv.addConsumable(consId);
      pickBtn.setText('✓ Pris !');
      pickBtn.setColor('#7f8c8d');
      pickBtn.removeInteractive();
      bg.setFillStyle(0x1a2a3a);
      this.recordPick();
    };

    pickBtn.on('pointerdown', () => {
      if (this.hasChosen || this.pickedConsumable) return;
      if (this.inv.getConsumables().length >= this.inv.getMaxConsumables()) {
        // Pool de consommables plein → ouvrir la modale et re-tenter
        const modal = new DiscardItemModal(this, this.inv, {
          category: 'consumables',
          onAfterDiscard: () => {
            if (this.inv.getConsumables().length < this.inv.getMaxConsumables()) doPickup();
          },
        });
        modal.show();
        return;
      }
      doPickup();
    });
    pickBtn.on('pointerover', () => { if (!this.hasChosen) pickBtn.setColor('#ffffff'); });
    pickBtn.on('pointerout', () => { if (!this.hasChosen) pickBtn.setColor(consFull ? '#e67e22' : '#f1c40f'); });
    ensureTouchTarget(pickBtn);
  }

  private drawInventorySummary(y: number): void {
    let summary = '--- Équipement équipe ---\n';

    const team = this.run.getTeam();
    for (const member of team) {
      if (!member.alive) continue;
      const equip = this.inv.getEquipment(member.id);
      const hasEquip = PERSONAL_SLOTS.some(s => equip[s] !== null);
      if (hasEquip) {
        summary += `${member.name}: `;
        const items = PERSONAL_SLOTS.filter(s => equip[s]).map(s => {
          const def = ITEM_DEFINITIONS[equip[s]!.definitionId];
          return def?.name ?? '?';
        });
        summary += items.join(', ') + '\n';
      }
    }

    summary += `Sac: ${this.inv.getTransportCount()} item(s)`;

    this.add.text(GAME_CX, y, summary, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      align: 'center', lineSpacing: 3,
    }).setOrigin(0.5, 0);
  }

  private confirmChoice(): void {
    // Si nextSceneAfter pointe sur RunVictoryScene (boss final), on saute EquipScene
    // pour aller directement à la victoire — évite le blocage en MapScene vide.
    startSceneWithFade(this, this.nextSceneAfter, { run: this.run });
  }

  /**
   * Appelé après chaque pick réussi. Incrémente le compteur et :
   * - Si quota atteint (boss = 2, autres = 1) → confirmChoice après 600ms
   * - Sinon → redessine l'UI (le subtitle et le skipBtn reflètent le nouveau compte)
   */
  private recordPick(): void {
    this.chosenCount++;
    if (this.chosenCount >= this.maxPicks) {
      this.time.delayedCall(600, () => this.confirmChoice());
      return;
    }
    // Boss avec 2 picks et un seul pris : on attend que l'utilisateur choisisse le 2e
    // ou clique "Terminer". Petit refresh des labels.
    this.time.delayedCall(400, () => this.drawUI());
  }
}
